// Transactions CTA analytics contract (design §9). This module is the ONLY
// place that constructs and sends these events -- it owns the injected,
// non-blocking transport (default no-op) so the UI never depends on
// delivery, never blocks a write, and never replays one. No external
// analytics provider is added here; wiring a real transport (or the app's
// shared event emitter, if one lands later) is `setTelemetryTransport`'s job,
// done outside this module (e.g. app bootstrap), never inside a component.
//
// Privacy contract (§9, enforced structurally by the typed event shapes
// below, not by a runtime scrub): no merchant name, free text, search
// string, rule sentence, amount, account number, raw error message,
// confidence value, or model metadata is ever a property on any event.
// Opaque IDs (transaction/line-item/category/job/draft/rule) are fine.

export type Surface = 'feed' | 'detail' | 'inbox' | 'tools';
export type ViewportBand = 'phone' | 'tablet' | 'desktop';
export type InputMethod = 'pointer' | 'keyboard' | 'touch' | 'unknown';

/**
 * Allowlisted error classes only -- never a raw error message/stack/code.
 * Callers must map their caught error to one of these via `toErrorClass`.
 */
export type ErrorClass =
  | 'network'
  | 'timeout'
  | 'conflict'
  | 'validation'
  | 'not_found'
  | 'server'
  | 'unknown';

export interface TelemetryContext {
  surface: Surface;
  viewport_band: ViewportBand;
  input_method: InputMethod;
}

/**
 * Maps `useShellContext().band` (`'mobile' | 'tablet' | 'desktop'`, the
 * shell's own naming) to this module's `ViewportBand` (`'phone' | 'tablet' |
 * 'desktop'`) -- the only translation needed since the two vocabularies
 * otherwise agree.
 */
export function viewportBandFromShellBand(band: 'mobile' | 'tablet' | 'desktop'): ViewportBand {
  return band === 'mobile' ? 'phone' : band;
}

interface BaseFields extends TelemetryContext {
  event_version: 1;
  interaction_id: string;
}

/** A fully-built event payload, ready for the transport. Index signature keeps this generic while every producer below is fully typed. */
export type TelemetryEvent = BaseFields & { event: string } & Record<string, unknown>;

export type TelemetryTransport = (event: TelemetryEvent) => void;

function noopTransport(): void {
  // Default: analytics is not wired to anything. Emitting must never throw
  // and never block/replay a write.
}

let transport: TelemetryTransport = noopTransport;

/** Injects the transport. Tests and (later) app bootstrap are the only intended callers. */
export function setTelemetryTransport(next: TelemetryTransport | null): void {
  transport = next ?? noopTransport;
}

export function resetTelemetryTransport(): void {
  transport = noopTransport;
  terminalJobDedupe.clear();
  searchDebounceTimers.forEach((timer) => clearTimeout(timer));
  searchDebounceTimers.clear();
  emittedSearchStates.clear();
}

/** Random interaction ID -- shared between an attempt and its settled result. */
export function newInteractionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Non-crypto fallback (older/test environments only) -- still opaque, never derived from user content.
  return `tid_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

/** Maps a caught error to the allowlisted class only -- never forwards message/code/status text. */
export function toErrorClass(error: unknown): ErrorClass {
  if (error && typeof error === 'object') {
    const maybeAxios = error as { isAxiosError?: boolean; response?: { status?: number } };
    if (maybeAxios.isAxiosError) {
      if (maybeAxios.response === undefined) return 'network';
      const status = maybeAxios.response.status;
      if (status === 408 || status === 504) return 'timeout';
      if (status === 409) return 'conflict';
      if (status === 404) return 'not_found';
      if (status === 400 || status === 422) return 'validation';
      if (typeof status === 'number' && status >= 500) return 'server';
    }
  }
  return 'unknown';
}

function emit(event: string, context: TelemetryContext, interactionId: string, props: Record<string, unknown>): void {
  const payload: TelemetryEvent = {
    event,
    event_version: 1,
    interaction_id: interactionId,
    surface: context.surface,
    viewport_band: context.viewport_band,
    input_method: context.input_method,
    ...props,
  };
  try {
    transport(payload);
  } catch {
    // Telemetry must never surface a delivery failure to the UI.
  }
}

// ---------------------------------------------------------------------------
// transactions_scope_flip / _result

export function emitScopeFlip(
  context: TelemetryContext,
  interactionId: string,
  props: {
    line_item_id: string;
    from_scope: 'personal' | 'business';
    to_scope: 'personal' | 'business';
    from_type_id: string | null;
    to_type_id: string | null;
    mapping: 'counterpart' | 'catch_all';
    version: number;
    /** §14 decision 3: whether the scope menu's "Apply to all items from {merchant}" checkbox was checked for this write. */
    apply_to_merchant: boolean;
  },
): void {
  emit('transactions_scope_flip', context, interactionId, props);
}

export function emitScopeFlipResult(
  context: TelemetryContext,
  interactionId: string,
  props: {
    outcome: 'saved' | 'conflict' | 'rejected' | 'unconfirmed';
    latency_ms: number;
    error_class: ErrorClass | null;
  },
): void {
  emit('transactions_scope_flip_result', context, interactionId, props);
}

// ---------------------------------------------------------------------------
// transactions_merchant_apply_result

/**
 * D-3: the outcome of the apply-to-similar POST chained onto a category/
 * scope write's "Apply to all items from {merchant}" checkbox (D-2:
 * applied synchronously). `applied_count` is the real count of OTHER items
 * relabeled, never a merchant name or any other free text (§9 privacy
 * contract).
 */
export function emitMerchantApplyOutcome(
  context: TelemetryContext,
  interactionId: string,
  props: {
    outcome: 'applied' | 'no_merchant' | 'failed' | 'unconfirmed';
    applied_count: number | null;
    latency_ms: number;
    error_class: ErrorClass | null;
  },
): void {
  emit('transactions_merchant_apply_result', context, interactionId, props);
}

// ---------------------------------------------------------------------------
// transactions_type_chooser_open / _search / _add

export function emitTypeChooserOpen(
  context: TelemetryContext,
  interactionId: string,
  props: {
    line_item_id: string;
    mode: 'scoped' | 'any';
    parent_id: string | null;
    presentation: 'popover' | 'sheet';
    entry: 'categorized' | 'uncategorized' | 'unknown' | 'transfer';
  },
): void {
  emit('transactions_type_chooser_open', context, interactionId, props);
}

export type QueryLengthBucket = '0' | '1_3' | '4_10' | '11_plus';

export function queryLengthBucket(length: number): QueryLengthBucket {
  if (length <= 0) return '0';
  if (length <= 3) return '1_3';
  if (length <= 10) return '4_10';
  return '11_plus';
}

const SEARCH_DEBOUNCE_MS = 300;
const searchDebounceTimers = new Map<string, ReturnType<typeof setTimeout>>();
// "once per distinct query state" -- dedupe key includes the chooser session so switching choosers doesn't cross-suppress.
const emittedSearchStates = new Set<string>();

/**
 * Debounces by 300ms and only emits once per distinct
 * (chooser_session_id, query_length_bucket, result_count, taxonomy_status)
 * state -- never the raw query string.
 */
export function emitTypeChooserSearchDebounced(
  context: TelemetryContext,
  interactionId: string,
  props: {
    chooser_session_id: string;
    query_length: number;
    result_count: number | null;
    taxonomy_status: 'ready' | 'unavailable';
  },
): void {
  const existing = searchDebounceTimers.get(props.chooser_session_id);
  if (existing) clearTimeout(existing);
  const timer = setTimeout(() => {
    searchDebounceTimers.delete(props.chooser_session_id);
    const bucket = queryLengthBucket(props.query_length);
    const stateKey = `${props.chooser_session_id}:${bucket}:${props.result_count}:${props.taxonomy_status}`;
    if (emittedSearchStates.has(stateKey)) return;
    emittedSearchStates.add(stateKey);
    emit('transactions_type_chooser_search', context, interactionId, {
      chooser_session_id: props.chooser_session_id,
      query_length_bucket: bucket,
      result_count: props.result_count,
      taxonomy_status: props.taxonomy_status,
    });
  }, SEARCH_DEBOUNCE_MS);
  searchDebounceTimers.set(props.chooser_session_id, timer);
}

export function emitTypeChooserAdd(
  context: TelemetryContext,
  interactionId: string,
  props: {
    stage: 'open' | 'submit' | 'created' | 'failed' | 'unconfirmed';
    parent_id: string | null;
    name_length_bucket: QueryLengthBucket | null;
    error_class: ErrorClass | null;
  },
): void {
  emit('transactions_type_chooser_add', context, interactionId, props);
}

// ---------------------------------------------------------------------------
// transactions_type_change / _result

export function emitTypeChange(
  context: TelemetryContext,
  interactionId: string,
  props: {
    line_item_id: string;
    from_type_id: string | null;
    to_type_id: string;
    from_scope: 'personal' | 'business' | null;
    to_scope: 'personal' | 'business';
    selection: 'suggested' | 'search' | 'browse' | 'created';
    /** §14 decision 3: whether the TypeChooser footer's "Apply to all items from {merchant}" checkbox was checked for this write. */
    apply_to_merchant: boolean;
  },
): void {
  emit('transactions_type_change', context, interactionId, props);
}

export function emitTypeChangeResult(
  context: TelemetryContext,
  interactionId: string,
  props: {
    outcome: 'saved' | 'conflict' | 'rejected' | 'unconfirmed';
    latency_ms: number;
    created_type: boolean;
    error_class: ErrorClass | null;
  },
): void {
  emit('transactions_type_change_result', context, interactionId, props);
}

// ---------------------------------------------------------------------------
// transactions_category_undo

export function emitCategoryUndo(
  context: TelemetryContext,
  interactionId: string,
  props: {
    stage: 'attempt' | 'saved' | 'conflict' | 'failed' | 'unconfirmed';
    original_interaction_id: string;
    line_item_id: string;
  },
): void {
  emit('transactions_category_undo', context, interactionId, props);
}

// ---------------------------------------------------------------------------
// transactions_merchant_offer_shown / _accept / _result / _dismiss --
// RETIRED (§14 decision 3): the bottom-right apply-to-merchant offer/tray
// surface is gone, replaced by the scope menu's/TypeChooser footer's
// "Apply to all items from {merchant}" checkbox on the scope-flip/
// type-change write itself (see `apply_to_merchant` on
// `transactions_scope_flip`/`transactions_type_change` above). No
// `transactions_merchant_offer_*` event is emitted anymore --
// telemetry.test.ts asserts none of these emitters exist.

// ---------------------------------------------------------------------------
// transactions_reclassify_start / _complete / _tracking_error

const terminalJobDedupe = new Set<string>();

export function emitReclassifyStart(
  context: TelemetryContext,
  interactionId: string,
  props: {
    job_id: string;
    scope: string;
    origin: 'tools' | 'merchant_apply' | 'rule_commit';
    deduped: boolean | null;
  },
): void {
  emit('transactions_reclassify_start', context, interactionId, props);
}

/**
 * "First observed terminal state for tracked job" -- deduplicated by
 * `(job_id, status)` across components/reloads, never emitted twice for
 * the same settled state.
 */
export function emitReclassifyComplete(
  context: TelemetryContext,
  interactionId: string,
  props: {
    job_id: string;
    outcome: 'done' | 'failed' | 'cancelled';
    processed: number | null;
    candidate_count: number | null;
    changed: number | null;
    counts_status: 'known' | 'partial' | 'unavailable';
    origin: 'tools' | 'merchant_apply' | 'rule_commit' | null;
    duration_ms: number | null;
    error_class: ErrorClass | null;
  },
): boolean {
  const key = `${props.job_id}:${props.outcome}`;
  if (terminalJobDedupe.has(key)) return false;
  terminalJobDedupe.add(key);
  emit('transactions_reclassify_complete', context, interactionId, { ...props, count_unit: 'line_items' });
  return true;
}

export function emitReclassifyTrackingError(
  context: TelemetryContext,
  interactionId: string,
  props: {
    job_id: string;
    last_known_status: string | null;
    error_class: ErrorClass;
  },
): void {
  emit('transactions_reclassify_tracking_error', context, interactionId, props);
}

// ---------------------------------------------------------------------------
// transactions_rule_draft / _commit

export function emitRuleDraft(
  context: TelemetryContext,
  interactionId: string,
  props: {
    stage: 'attempt' | 'ready' | 'unresolved' | 'failed';
    draft_id: string | null;
    draft_kind: 'event' | 'merchant' | 'keyword' | 'account' | null;
    preview_count: number | null;
    sample_count: number | null;
    error_class: ErrorClass | null;
  },
): void {
  emit('transactions_rule_draft', context, interactionId, { ...props, count_unit: 'line_items' });
}

export function emitRuleCommit(
  context: TelemetryContext,
  interactionId: string,
  props: {
    stage: 'attempt' | 'queued' | 'expired' | 'conflict' | 'failed' | 'unconfirmed';
    draft_id: string;
    hard: boolean;
    saved_object: 'context' | 'rule' | 'context_and_rule' | null;
    job_id: string | null;
    rule_id: string | null;
    error_class: ErrorClass | null;
  },
): void {
  emit('transactions_rule_commit', context, interactionId, props);
}
